/**
 * Logger Utility
 *
 * Provides structured logging with different log levels.
 * Replaces direct console.log usage throughout the application.
 *
 * Usage:
 *   import logger from '../utils/logger';
 *
 *   logger.debug('Component mounted', { props });
 *   logger.info('User action', { action: 'submit' });
 *   logger.warn('Deprecated feature used');
 *   logger.error('API call failed', error);
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

class Logger {
  constructor() {
    // Set log level from environment variable
    // In production, this should be INFO or higher
    // In development, can be DEBUG
    this.level = this.getLogLevel();
    this.isDevelopment = process.env.NODE_ENV === 'development';

    // Sensitive keys to redact from logs
    this.sensitiveKeys = [
      'password',
      'token',
      'apiKey',
      'api_key',
      'accessToken',
      'access_token',
      'refreshToken',
      'refresh_token',
      'secret',
      'authorization',
      'auth',
      'credit_card',
      'creditCard',
      'ssn',
      'social_security'
    ];
  }

  /**
   * Sanitize sensitive data from objects before logging
   */
  sanitize(data) {
    if (!data || typeof data !== 'object') {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitize(item));
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();

      // Check if this key contains sensitive data
      const isSensitive = this.sensitiveKeys.some(sensitiveKey =>
        lowerKey.includes(sensitiveKey.toLowerCase())
      );

      if (isSensitive) {
        // Redact sensitive values
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = this.sanitize(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  getLogLevel() {
    const envLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();

    // Default to DEBUG in development, INFO in production
    if (!envLevel) {
      return process.env.NODE_ENV === 'development'
        ? LOG_LEVELS.DEBUG
        : LOG_LEVELS.INFO;
    }

    return LOG_LEVELS[envLevel] ?? LOG_LEVELS.INFO;
  }

  formatMessage(level, message, data) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level}]`;

    if (data !== undefined) {
      return { prefix, message, data };
    }

    return { prefix, message };
  }

  shouldLog(level) {
    return LOG_LEVELS[level] >= this.level;
  }

  debug(message, data) {
    if (!this.shouldLog('DEBUG')) return;

    const sanitizedData = this.sanitize(data);
    const formatted = this.formatMessage('DEBUG', message, sanitizedData);

    if (this.isDevelopment) {
      console.log(`${formatted.prefix} ${formatted.message}`, formatted.data ?? '');
    }
  }

  info(message, data) {
    if (!this.shouldLog('INFO')) return;

    const sanitizedData = this.sanitize(data);
    const formatted = this.formatMessage('INFO', message, sanitizedData);
    console.info(`${formatted.prefix} ${formatted.message}`, formatted.data ?? '');
  }

  warn(message, data) {
    if (!this.shouldLog('WARN')) return;

    const sanitizedData = this.sanitize(data);
    const formatted = this.formatMessage('WARN', message, sanitizedData);
    console.warn(`${formatted.prefix} ${formatted.message}`, formatted.data ?? '');
  }

  error(message, error) {
    if (!this.shouldLog('ERROR')) return;

    // Sanitize error data but preserve Error properties
    let sanitizedError = error;
    if (error instanceof Error) {
      sanitizedError = {
        message: error.message,
        name: error.name,
        // Only include stack trace in development
        ...(this.isDevelopment && { stack: error.stack })
      };
    } else if (typeof error === 'object') {
      sanitizedError = this.sanitize(error);
    }

    const formatted = this.formatMessage('ERROR', message, sanitizedError);
    console.error(`${formatted.prefix} ${formatted.message}`, formatted.data ?? '');
  }

  // Group related logs together (useful for debugging complex operations)
  group(label) {
    if (!this.isDevelopment) return;
    console.group(label);
  }

  groupEnd() {
    if (!this.isDevelopment) return;
    console.groupEnd();
  }

  // Table display for array data (useful for debugging)
  table(data) {
    if (!this.isDevelopment) return;
    console.table(data);
  }

  // Performance timing
  time(label) {
    if (!this.isDevelopment) return;
    console.time(label);
  }

  timeEnd(label) {
    if (!this.isDevelopment) return;
    console.timeEnd(label);
  }
}

// Create singleton instance
const logger = new Logger();

// Expose log level constants
logger.LOG_LEVELS = LOG_LEVELS;

export default logger;

/**
 * Configuration:
 *
 * Set log level in .env:
 *   REACT_APP_LOG_LEVEL=DEBUG   # Show all logs
 *   REACT_APP_LOG_LEVEL=INFO    # Show info, warn, error
 *   REACT_APP_LOG_LEVEL=WARN    # Show warn and error only
 *   REACT_APP_LOG_LEVEL=ERROR   # Show errors only
 *   REACT_APP_LOG_LEVEL=NONE    # Disable all logging
 *
 * Migration from console.log:
 *
 *   Before: console.log('User logged in:', user);
 *   After:  logger.info('User logged in', { user });
 *
 *   Before: console.error('API error:', error);
 *   After:  logger.error('API error', error);
 *
 *   Before: console.warn('Deprecated');
 *   After:  logger.warn('Deprecated feature used');
 *
 *   Before: console.log('Debug:', data);
 *   After:  logger.debug('Debug info', { data });
 */
